import { supabase } from "@/utils/supabase";
import { initializePaddle } from "@paddle/paddle-js";

// 버츄패스(1회 기간제). billing_purchase(now ∈ [started_at, ended_at])가 단일 진실 출처.
// 결제는 Paddle Checkout(overlay) → paddle webhook이 billing_purchase 적재.
//  (구 PortOne 반복결제 + 펫티어(pet_limit) 흐름은 제거됨)

// Paddle.js 인스턴스 (1회 초기화 후 재사용). 토큰 접두사(test_/live_)로 sandbox/production 자동 판별.
let paddlePromise = null;
let onCheckoutCompleted = null;

function isSandbox() {
  return (process.env.VUE_APP_PADDLE_CLIENT_TOKEN || "").startsWith("test_");
}

function getPaddle() {
  if (!paddlePromise) {
    paddlePromise = initializePaddle({
      environment: isSandbox() ? "sandbox" : "production",
      token: process.env.VUE_APP_PADDLE_CLIENT_TOKEN,
      eventCallback: (event) => {
        if (event.name === "checkout.completed" && onCheckoutCompleted) onCheckoutCompleted(event);
      },
    });
  }
  return paddlePromise;
}

export default {
  // 현재 버츄패스 보유 여부 + 만료일 (active billing_purchase 없으면 미보유)
  async fetchActivePlan() {
    const nowIso = new Date().toISOString();
    const { data } = await supabase
      .from("billing_purchase")
      .select("id, started_at, ended_at, source")
      .lte("started_at", nowIso)
      .gte("ended_at", nowIso)
      .order("ended_at", { ascending: false })
      .limit(1);
    const active = data && data[0];
    return { active: !!active, expiredAt: active ? active.ended_at : null };
  },

  // 버츄패스 상태 (fetchActivePlan 별칭)
  fetchPassStatus() {
    return this.fetchActivePlan();
  },

  // 구매내역 (본인 billing_purchase, 최신순). RLS로 본인 행만.
  async fetchPurchaseHistory() {
    const { data } = await supabase
      .from("billing_purchase")
      .select("id, amount, started_at, ended_at, source")
      .order("started_at", { ascending: false });
    return data || [];
  },

  // 환불신청 (폼 → billing_refund_request 적재, 운영자 수동 처리)
  async requestRefund(content) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("E_NOT_LOGGED_IN");
    const { error } = await supabase.from("billing_refund_request").insert({
      auth_uuid: session.user.id,
      user_email: session.user.email,
      content,
    });
    if (error) throw error;
  },

  // 결제 연동(Paddle) 준비 여부 — 구매 버튼 활성/비활성용 (상품 유무는 fetchVirtupassProduct로 별도 확인)
  isCheckoutReady() {
    return !!process.env.VUE_APP_PADDLE_CLIENT_TOKEN;
  },

  // 상품 조회 (billing_product SSOT, RLS로 active만 노출). priceId는 클라 환경(sandbox/live)에 맞는 컬럼.
  async fetchProduct(productId) {
    const { data } = await supabase
      .from("billing_product")
      .select("id, name, type, meta, paddle_price_id, paddle_sandbox_price_id")
      .eq("id", productId)
      .maybeSingle();
    if (!data) return null;
    const priceId = isSandbox() ? data.paddle_sandbox_price_id : data.paddle_price_id;
    return priceId ? { ...data, priceId } : null;
  },

  // 상품 구매 — Paddle overlay checkout. productId는 앱이 지정(예: 'VIRTUPASS_30').
  // 결제 완료 적재는 paddle webhook 담당(수 초 지연), onCompleted는 overlay상 결제 성공 시점 콜백.
  async startCheckout(productId, { onCompleted } = {}) {
    if (!this.isCheckoutReady()) throw new Error("E_PADDLE_NOT_READY");
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("E_NOT_LOGGED_IN");

    const product = await this.fetchProduct(productId);
    if (!product) throw new Error("E_PADDLE_NOT_READY");

    const paddle = await getPaddle();
    if (!paddle) throw new Error("E_PADDLE_INIT_FAILED");

    onCheckoutCompleted = onCompleted || null;
    paddle.Checkout.open({
      items: [{ priceId: product.priceId, quantity: 1 }],
      // webhook이 custom_data.user_id로 사용자 식별 — 필수
      customData: { user_id: session.user.id, user_email: session.user.email },
      customer: { email: session.user.email },
    });
  },
};
